import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, TrendingUp, BarChart3, ChevronRight, Loader2 } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'

const API = process.env.REACT_APP_BACKEND_URL

function fmt(n) {
  if (n == null) return '--'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
}
function fmtPct(n) {
  if (n == null) return '--'
  return `${Number(n).toFixed(1)}%`
}
function formatDate(d) {
  if (!d) return '--'
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function DAFPolices() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const tenantId = profile?.tenant_id
  const [polices, setPolices] = useState([])
  const [spData, setSpData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) return
    Promise.all([
      fetch(`${API}/api/polices?tenant_id=${tenantId}`).then(r => r.json()),
      fetch(`${API}/api/budget/sp-complet/${tenantId}`).then(r => r.json()),
    ]).then(([polData, sp]) => {
      setPolices(polData.polices || polData.data || [])
      setSpData(sp)
    }).catch(console.error)
    .finally(() => setLoading(false))
  }, [tenantId])

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
  }

  const spByPolice = {}
  if (spData?.sp_par_police) {
    for (const p of spData.sp_par_police) {
      spByPolice[p.police_id] = p
    }
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="daf-polices">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Polices d'assurance</h1>
        <p className="text-sm text-slate-500 mt-0.5">Vue S/P par police — exercice en cours</p>
      </div>

      {/* Global KPIs */}
      {spData && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">Prime HT totale</p>
            <p className="text-xl font-bold text-slate-900">{fmt(spData.prime_ht)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">Charge sinistres</p>
            <p className="text-xl font-bold text-slate-900">{fmt(spData.charge_sinistres)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">S/P reel</p>
            <p className={`text-xl font-bold ${(spData.sp_reel || 0) > 80 ? 'text-red-600' : (spData.sp_reel || 0) > 60 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {fmtPct(spData.sp_reel)}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">S/P annualise</p>
            <p className={`text-xl font-bold ${(spData.sp_annualise || 0) > 80 ? 'text-red-600' : (spData.sp_annualise || 0) > 60 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {fmtPct(spData.sp_annualise)}
            </p>
          </div>
        </div>
      )}

      {/* Polices list */}
      <div className="space-y-3">
        {polices.map(p => {
          const sp = spByPolice[p.id] || {}
          const spReel = sp.sp_reel || 0
          const spColor = spReel > 80 ? 'text-red-600' : spReel > 60 ? 'text-amber-600' : 'text-emerald-600'
          const barColor = spReel > 80 ? 'bg-red-500' : spReel > 60 ? 'bg-amber-500' : 'bg-emerald-500'
          return (
            <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-300 transition-colors" data-testid="daf-police-card">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                  <Shield size={16} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-800">{p.libelle || p.numero_police}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.statut === 'en_cours' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.statut === 'en_cours' ? 'En cours' : p.statut}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{p.compagnies?.nom || '--'}</span>
                    <span>{formatDate(p.date_effet)} - {formatDate(p.date_echeance)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-6 text-right shrink-0">
                  <div>
                    <p className="text-xs text-slate-400">Prime HT</p>
                    <p className="text-sm font-semibold text-slate-700">{fmt(sp.prime_ht)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Charge</p>
                    <p className="text-sm font-semibold text-slate-700">{fmt(sp.charge_sinistres)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">S/P reel</p>
                    <p className={`text-sm font-bold ${spColor}`}>{fmtPct(sp.sp_reel)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">S/P annualise</p>
                    <p className={`text-sm font-bold ${spColor}`}>{fmtPct(sp.sp_annualise)}</p>
                  </div>
                </div>
              </div>
              {/* S/P bar */}
              {sp.prime_ht > 0 && (
                <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(spReel, 100)}%` }} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
